import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, CircleAlert, Clock, Globe, Loader2, MailCheck, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { EMAIL_TEST_STATUS, FINAL_EMAIL_TEST_STATUSES, getEmailTest, listEmailTests, sendEmailTest } from '../../lib/adminApi';
import { apiErrorMessage } from '../../lib/projectApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

const POLL_MS = 3000;
const POLL_FOR_MS = 90000;

const TONE_BADGE = {
  wait: 'bg-primary/15 text-primary hover:bg-primary/15',
  ok: 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))] hover:bg-[hsl(var(--wp-context-green))]/15',
  warn: 'bg-[hsl(var(--wp-context-amber))]/20 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/20',
  bad: 'bg-destructive/15 text-destructive hover:bg-destructive/15',
};

const statusOf = (test) => EMAIL_TEST_STATUS[test?.status] || { label: test?.status || '—', detail: '', tone: 'wait' };

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)} h ago`;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Accepted → Sent → Delivered, with the live one pulsing; a bad outcome replaces the last step. */
const Steps = ({ test }) => {
  const s = statusOf(test);
  const order = ['accepted', 'sent', 'delivered'];
  const reached = s.tone === 'ok' ? 3 : test.status === 'sent' ? 2 : 1;
  const bad = s.tone === 'bad';
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label="Delivery progress" data-testid="email-test-steps">
      {order.map((key, i) => {
        const done = i < reached;
        const live = !bad && i === reached - 1 && s.tone !== 'ok';
        const failedHere = bad && i === 2;
        const label = failedHere ? s.label : EMAIL_TEST_STATUS[key].label;
        return (
          <React.Fragment key={key}>
            {i > 0 && <span className={`h-px flex-1 ${done || failedHere ? 'bg-primary/50' : 'bg-border'}`} aria-hidden="true" />}
            <li className={`inline-flex items-center gap-1.5 whitespace-nowrap ${failedHere ? 'text-destructive' : done ? 'text-foreground' : 'text-muted-foreground'}`} data-testid={`email-test-step-${key}`} aria-current={live ? 'step' : undefined}>
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${failedHere ? 'border-destructive bg-destructive/10' : done ? (s.tone === 'ok' && i === 2 ? 'border-[hsl(var(--wp-context-green))] bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))]' : 'border-primary bg-primary/10 text-primary') : 'border-border'}`} aria-hidden="true">
                {failedHere ? <CircleAlert className="h-3 w-3" /> : live ? <Loader2 className="h-3 w-3 animate-spin" /> : done ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-border" />}
              </span>
              {label}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
};

/**
 * Account ▸ Send me a test email (owner accounts only — OWNER_EMAILS on the server). One click sends a branded
 * delivery test to the owner's own address from the real sender; the dialog then polls the server, which reads
 * Resend's delivery event, until it is delivered (or bounced). Recent tests are listed below so a post-deploy check
 * is a glance away.
 */
export const EmailTestDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState(null);
  const [sending, setSending] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);
  const startedAt = useRef(0);

  const stopPolling = useCallback(() => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } setPolling(false); }, []);

  const loadHistory = useCallback(async () => {
    try { setHistory(await listEmailTests()); } catch (e) { setHistory([]); setError(await apiErrorMessage(e, 'Recent tests could not be loaded.')); }
  }, []);

  const poll = useCallback(async (id) => {
    try {
      const t = await getEmailTest(id);
      setCurrent(t);
      setHistory((h) => (h ? h.map((x) => (x.id === t.id ? t : x)) : h));
      const finished = FINAL_EMAIL_TEST_STATUSES.includes(t.status) || !t.tracking;
      if (finished || Date.now() - startedAt.current > POLL_FOR_MS) {
        stopPolling();
        if (t.status === 'delivered') toast.success('Test email delivered', { description: `Resend confirmed delivery to ${t.to}.`, id: 'email-test' });
        else if (statusOf(t).tone === 'bad') toast.error(`Test email ${statusOf(t).label.toLowerCase()}`, { description: statusOf(t).detail, id: 'email-test' });
        return;
      }
      timer.current = setTimeout(() => poll(id), POLL_MS);
    } catch (e) {
      stopPolling();
      setError(await apiErrorMessage(e, 'The delivery status could not be read.'));
    }
  }, [stopPolling]);

  const beginPolling = useCallback((t) => {
    stopPolling();
    if (!t || FINAL_EMAIL_TEST_STATUSES.includes(t.status) || !t.tracking) return;
    startedAt.current = Date.now();
    setPolling(true);
    timer.current = setTimeout(() => poll(t.id), POLL_MS);
  }, [poll, stopPolling]);

  useEffect(() => {
    if (!open) { stopPolling(); return undefined; }
    setError('');
    loadHistory();
    return stopPolling;
  }, [open, loadHistory, stopPolling]);

  const send = async () => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const t = await sendEmailTest();
      setCurrent(t);
      setHistory((h) => [t, ...(h || []).filter((x) => x.id !== t.id)].slice(0, 8));
      toast.success('Test email sent', { description: `On its way to ${t.to} — watching for delivery…`, id: 'email-test' });
      beginPolling(t);
    } catch (e) {
      setError(await apiErrorMessage(e, 'The test email could not be sent.'));
    } finally {
      setSending(false);
    }
  };

  const recheck = () => { if (current && !polling) { setError(''); startedAt.current = Date.now(); setPolling(true); poll(current.id); } };

  const s = current ? statusOf(current) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] grid-cols-[minmax(0,1fr)] max-h-[90vh] overflow-y-auto bg-card" data-testid="email-test-dialog">
        <DialogHeader>
          <DialogTitle style={heading}>Send me a test email</DialogTitle>
          <DialogDescription>Confirms that account emails reach a real inbox from the verified sending domain — handy right after a deploy.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-4" data-testid="email-test-panel">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground break-all" data-testid="email-test-to">To {user?.email}</div>
                <p className="mt-1 text-xs text-muted-foreground">A branded “delivery test” with the environment, host, sender, reply-to and time it was sent.</p>
              </div>
              {s && <Badge className={`shrink-0 ${TONE_BADGE[s.tone]}`} data-testid="email-test-status-badge">{s.label}</Badge>}
            </div>
            {current && (
              <div className="mt-3 border-t border-border pt-3 space-y-3">
                <Steps test={current} />
                <p className="text-xs text-muted-foreground" data-testid="email-test-status-detail">{s.detail}{!current.tracking && ' Delivery events are only available on the Resend transport.'}</p>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs" data-testid="email-test-facts">
                  <dt className="text-muted-foreground inline-flex items-center gap-1"><Globe className="h-3 w-3" aria-hidden="true" />Environment</dt><dd className="text-foreground truncate">{current.environment} · {current.host}</dd>
                  <dt className="text-muted-foreground inline-flex items-center gap-1"><Send className="h-3 w-3" aria-hidden="true" />Sent from</dt><dd className="text-foreground truncate">{current.sender}</dd>
                  <dt className="text-muted-foreground inline-flex items-center gap-1"><MailCheck className="h-3 w-3" aria-hidden="true" />Reply-To</dt><dd className="text-foreground truncate">{current.reply_to || '—'}</dd>
                  <dt className="text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" aria-hidden="true" />Transport</dt><dd className="text-foreground truncate">{current.transport === 'resend' ? 'Resend · own domain' : 'Emergent email proxy'}</dd>
                  <dt className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden="true" />Sent</dt><dd className="text-foreground truncate">{new Date(current.sent_at).toLocaleString()}</dd>
                </dl>
              </div>
            )}
          </div>
          {error && <p className="text-xs text-destructive flex items-start gap-1.5 break-words [overflow-wrap:anywhere]" role="alert" data-testid="email-test-error"><CircleAlert className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />{error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="h-10" onClick={send} disabled={sending || polling} data-testid="email-test-send">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4 mr-1.5" aria-hidden="true" />}{sending ? 'Sending…' : current ? 'Send another' : 'Send test email'}
            </Button>
            {current && current.tracking && !FINAL_EMAIL_TEST_STATUSES.includes(current.status) && (
              <Button type="button" variant="outline" className="h-10" onClick={recheck} disabled={polling} data-testid="email-test-recheck"><RefreshCw className={`h-4 w-4 mr-1.5 ${polling ? 'animate-spin' : ''}`} aria-hidden="true" />{polling ? 'Watching…' : 'Check again'}</Button>
            )}
          </div>
          <div data-testid="email-test-history">
            <div className="text-xs font-semibold text-foreground mb-1.5">Recent tests</div>
            {history === null ? (
              <div className="space-y-1.5"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-5/6" /></div>
            ) : history.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="email-test-history-empty">No tests yet — send the first one above.</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {history.map((t) => {
                  const st = statusOf(t);
                  return (
                    <li key={t.id} className="flex min-w-0 items-center justify-between gap-3 px-2.5 py-1.5 text-xs" data-testid="email-test-history-item">
                      <span className="min-w-0 truncate text-muted-foreground"><span className="text-foreground">{t.environment}</span> · {t.host} · {formatWhen(t.sent_at)}</span>
                      <Badge className={`shrink-0 ${TONE_BADGE[st.tone]}`}>{st.label}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>Only owner accounts see this tool. Tests go to your own address only, at most five an hour, and are kept for 30 days.</span></p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
